import React, { useState, useEffect } from "react";

import { useTranslation } from "react-i18next";
import * as yup from "yup";

import { ActionButtonProps } from "../../../../../../../../components/Generic/ActionButtons";
import Modal, { StatusFieldProps } from "../../../../../../../../components/Generic/Modal";
import { NotificationToast } from "../../../../../../../../components/Generic/Toast/NotificationToast";
import UISteps, { Step } from "../../../../../../../../components/Generic/UISteps";
import {
  TypeOfButton,
  useStepsAction,
} from "../../../../../../../../components/Generic/UISteps/useActions";
import UITabMaterial, {
  PanelProps,
} from "../../../../../../../../components/Generic/UITabMaterial";
import { useAppState } from "../../../../../../../../context/StateContext";
import useYupValidation from "../../../../../../../../hooks/useYupValidation";
import { Branch } from "../../../../../../../../models/branch";
import { Contact } from "../../../../../../../../models/contact";
import { DifferentLocation } from "../../../../../../../../models/location";
import { Warehouse } from "../../../../../../../../models/warehouse";
import { Status } from "../../../../../../../../util/enums";
import Branches from "../components/Branches";
import Contacts from "../components/Contacts";
import General from "../components/General";
import { SectionTab } from "../components/Sections";
import useStyles from "./styles";
import { Tabs, useWarehouseActions } from "./useActions";

interface Props {
  warehouseToUpdate?: Warehouse;
  isOnViewMode?: boolean;
  open: boolean;
  hasError?: boolean;
  hasCodeError?: boolean;
  setHasCodeError?: (hasCodeError: boolean) => void;
  isLoading?: boolean;
  closeModal: () => void;
  submit: (warehouse: Warehouse) => void;
}

export interface ValidationErrorPersonalized {
  errors: yup.ValidationError[];
  index: number;
}

interface ErrorsPath {
  code: string;
  name: string;
  location: string;
  branches: string;
  sections: string;
}

const WarehouseModal = ({
  warehouseToUpdate,
  open,
  hasError,
  hasCodeError,
  setHasCodeError,
  isLoading = false,
  isOnViewMode = false,
  closeModal,
  submit,
}: Props) => {
  const classes = useStyles();
  const { currentUser } = useAppState();
  const { t: translate } = useTranslation();

  const {
    warehouse,
    errors,
    options,
    currentStep,
    isUsingCompanyAddress,
    selectLeftPanel,
    setSelectLeftPanel,
    setIsUsingCompanyAddress,
    companyLocation,
    setCompanyLocation,
    handleUpdateWarehouseLocation,
    handleClearFields,
    handleUpdateWarehouse,
    handleValidate,
    nextStep,
    previousStep,
    warehouseStepHasError,
    sectionStepHasError,
    branchStepHasError,
  } = useWarehouseActions();
  const { onCancel, onFinish, setButton } = useStepsAction();
  const { fieldHasError, getErrorByField } = useYupValidation(errors);

  const [isSubmitted, setIsSubmitted] = useState<boolean>(false);
  const [isSubmitDisabled, setIsSubmitDisabled] = useState<boolean>(false);
  const [isCurrentStepSubmitted, setIsCurrentStepSubmitted] = useState<boolean>(false);
  const [errorsSections] = useState<ValidationErrorPersonalized[]>([]);

  const errorsPaths: ErrorsPath = {
    code: translate("code"),
    name: translate("name"),
    branches: translate("branches"),
    sections: translate("sections"),
    location: translate("address"),
  };

  const schema = yup.object().shape({
    code: yup.string().required(translate("thisFieldIsRequired")),
    name: yup.string().required(translate("thisFieldIsRequired")),
    description: yup.string().nullable(),
    hasBranch: yup.boolean(),
    branches: yup.array().when("hasBranch", {
      is: true,
      then: yup
        .array()
        .of(yup.object())
        .min(1, translate("youNeedToSelectAtLeastOneBranch"))
        .required(translate("youNeedToSelectAtLeastOneBranch")),
    }),
    location: yup
      .object({
        longAddress: yup.string().required(translate("thisFieldIsRequired")),
      })
      .required(translate("thisFieldIsRequired"))
      .typeError(translate("thisFieldIsRequired")),
    hasSection: yup.boolean(),
    sections: yup.array().when("hasSection", {
      is: true,
      then: yup
        .array()
        .of(
          yup.object().shape({
            name: yup
              .string()
              .required(translate("thisFieldIsRequired"))
              .typeError(translate("thisFieldIsRequired")),
            containsField: yup
              .bool()
              .isTrue(translate("thisFieldIsRequired"))
              .typeError(translate("thisFieldIsRequired")),
            allowsItemLoose: yup.boolean(),
            allowsStorageBox: yup.boolean(),
            isItemLooseOnly: yup.boolean(),
            storageBoxTypes: yup.array().when(["allowsItemLoose", "allowsStorageBox"], {
              is: (allowsItemLoose: boolean, allowsStorageBox: boolean) =>
                !allowsItemLoose && allowsStorageBox,
              then: yup
                .array()
                .min(1, translate("thisFieldIsRequired"))
                .required(translate("thisFieldIsRequired")),
            }),
            isDefault: yup.boolean(),
          })
        )
        .min(1, translate("youNeedToSelectAtLeastOneSection"))
        .required(translate("youNeedToSelectAtLeastOneSection")),
    }),
  });

  const handleDisableSubmitButton = () => {
    if (
      isLoading ||
      (options.length >= countSteps.length && errorsSections.length > 0) ||
      !handleValidate(schema)
    ) {
      setIsSubmitDisabled(true);
    } else {
      setIsSubmitDisabled(false);
    }
  };

  const handleSubmit = () => {
    handleDisableSubmitButton();
    setIsSubmitted(true);
    setIsCurrentStepSubmitted(true);
    onFinish(
      handleValidate(schema),
      !!warehouseToUpdate,
      options,
      countSteps,
      () => submit({ ...warehouse }),
      nextStep
    );
  };

  const handleCloseModal = () => {
    handleClearFields();
    closeModal();
  };

  const handleSetButton = (button: string) => {
    return setButton({
      type: button,
      isToUpdate: !!warehouseToUpdate,
      options,
      steps: countSteps,
    });
  };

  const setModalText = (): string => {
    if (warehouseToUpdate) {
      if (isOnViewMode) return translate("viewWarehouse");

      return translate("editWarehouse");
    }

    return translate("createANewWarehouse");
  };

  const generalTab: Step = {
    label: translate("warehouse"),
    dataCy: "warehouseStep",
    key: Tabs.WAREHOUSE,
    required: true,
    hasError:
      !!(
        getErrorByField("name") ||
        getErrorByField("code") ||
        getErrorByField("description") ||
        getErrorByField("location.longAddress") ||
        getErrorByField("location")
      ) || hasCodeError,
    component: (
      <General
        warehouse={warehouse}
        isLoading={isLoading}
        isOnViewMode={isOnViewMode}
        companyLocation={companyLocation}
        hasCodeError={hasCodeError}
        setHasCodeError={setHasCodeError}
        isUsingCompanyAddress={isUsingCompanyAddress}
        setIsUsingCompanyAddress={setIsUsingCompanyAddress}
        updateUser={(value) =>
          handleUpdateWarehouseLocation(
            value,
            currentUser?.company?.location || ({} as DifferentLocation)
          )
        }
        getErrorByField={(value) => getErrorByField(value)}
        setWarehouse={(warehouse) => handleUpdateWarehouse(warehouse)}
      />
    ),
  };

  const branchesTab: Step = {
    label: translate("branches"),
    dataCy: "branchStep",
    key: Tabs.BRANCHES,
    required: true,
    hasError: !!getErrorByField("branches"),
    component: (
      <Branches
        isLoading={isLoading}
        branchesList={warehouse?.branches || ([] as Branch[])}
        errorMessage={getErrorByField("branches")}
        error={fieldHasError("branches")}
        updateBranchesOnWarehouse={(branches: Branch[]) =>
          handleUpdateWarehouse({ ...warehouse, branches: [...branches] })
        }
        isOnViewMode={isOnViewMode}
      />
    ),
  };

  const sectionsTab: Step = {
    label: translate("sections"),
    dataCy: "sectionStep",
    key: Tabs.SECTIONS,
    required: true,
    hasError: !!getErrorByField("sections") || errorsSections.length > 0 || sectionStepHasError,
    component: (
      <SectionTab
        errors={errors}
        sectionList={warehouse?.sections || []}
        onViewMode={isOnViewMode}
        onUpdate={(sections) => handleUpdateWarehouse({ ...warehouse, sections: [...sections] })}
      />
    ),
  };

  const contactsTab: Step = {
    label: translate("contacts"),
    dataCy: "contactStep",
    key: Tabs.CONTACTS,
    action:
      options.includes(Tabs.SECTIONS) && !fieldHasError("sections") && !sectionStepHasError
        ? () => nextStep(Tabs.CONTACTS)
        : undefined,
    hasError: false,
    component: (
      <Contacts
        contactsList={warehouse?.contacts || ([] as Contact[])}
        updateContactsOnWarehouse={(contacts: Contact[]) =>
          handleUpdateWarehouse({ ...warehouse, contacts: [...contacts] })
        }
        isOnViewMode={isOnViewMode || isLoading}
      />
    ),
  };

  const steps: Step[] = [generalTab, branchesTab, sectionsTab, contactsTab];
  const countSteps: Step[] = [generalTab, branchesTab, sectionsTab];

  const actionButtonProps: ActionButtonProps = {
    dataCySave: "submitButton",
    dataCyCancel: "cancelButton",
    textSave: handleSetButton(TypeOfButton.SUBMIT).text,
    textCancel: handleSetButton(TypeOfButton.CANCEL).text,
    customColorSave: handleSetButton(TypeOfButton.SUBMIT).color,
    customColorCancel: handleSetButton(TypeOfButton.CANCEL).color,
    loading: isLoading,
    saveDisabled: isSubmitDisabled,
    onSave: isOnViewMode ? undefined : () => handleSubmit(),
    onCancel: () => {
      onCancel(options, !!warehouseToUpdate, handleCloseModal, previousStep);
      handleDisableSubmitButton();
    },
  };

  const statusFieldProps: StatusFieldProps = {
    label: translate("warehouseStatus"),
    value: warehouse?.status || Status.INACTIVE,
    dataCy: "selectStatus",
    placeholder: translate("selectAnOption"),
    disabled: isOnViewMode || isLoading,
    handleChange: (value) => handleUpdateWarehouse({ ...warehouse, status: value }),
  };

  const panels: PanelProps[] = steps.map((step) => ({
    menuItem: step.label,
    className: classes.modalTabBody,
    render: () => step.component,
  }));

  const checkIfCurrentStepHasError = () => {
    return (
      (currentStep === Tabs.WAREHOUSE && warehouseStepHasError) ||
      (currentStep === Tabs.BRANCHES && branchStepHasError) ||
      (currentStep === Tabs.SECTIONS && sectionStepHasError)
    );
  };

  useEffect(() => {
    if (warehouseToUpdate?.locationId) {
      if (currentUser && currentUser.company && currentUser.company.location) {
        if (warehouseToUpdate?.locationId === currentUser.company.location.id)
          setIsUsingCompanyAddress(true);
      }
    }
  }, []);

  useEffect(() => {
    if (isSubmitted || warehouse?.id) {
      handleDisableSubmitButton();
      handleValidate(schema);
    }

    if (warehouse) {
      if (currentUser && currentUser.company && currentUser.company.location) {
        if (warehouse.locationId === currentUser?.company?.location?.id) {
          setCompanyLocation(warehouse.location);
        }
      }
    }
  }, [warehouse]);

  useEffect(() => {
    if (warehouseToUpdate) handleUpdateWarehouse({ ...warehouseToUpdate });
  }, [warehouseToUpdate]);

  useEffect(() => {
    if (hasError !== undefined && !hasError) handleCloseModal();
  }, [hasError]);

  useEffect(() => {
    if (checkIfCurrentStepHasError()) {
      setIsSubmitDisabled(true);
    } else if (currentStep !== Tabs.CONTACTS) {
      setIsSubmitDisabled(false);
    } else {
      handleDisableSubmitButton();
    }
  }, [currentStep]);

  useEffect(() => {
    if (isSubmitDisabled && isCurrentStepSubmitted && checkIfCurrentStepHasError()) {
      const errorsPathsNames = errors?.map((error) => error.path?.split(".")[0]).join(", ");
      const groupedErrors: string[] = [];

      Object.keys(errorsPaths).forEach((errorName) => {
        if (errorsPathsNames.includes(errorName)) {
          groupedErrors.push(errorsPaths[errorName as keyof ErrorsPath]);
        }
      });

      if (groupedErrors.length > 0) {
        NotificationToast.warning(
          `${translate("someRequiredInformationAreMissing")}: ${groupedErrors.join(", ")}`,
          {
            id: "warehouse.validation.warning.missing.required.information",
          }
        );
      }
    }
    setIsCurrentStepSubmitted(false);
  }, [isSubmitDisabled, isCurrentStepSubmitted]);

  return (
    <Modal
      data-cy="warehouseModal"
      open={open}
      handleClose={closeModal}
      showActionButtons
      actionButtonProps={actionButtonProps}
      title={setModalText()}
      labelStatus={translate("status")}
      bodyClassName={!warehouseToUpdate ? classes.modalSection : ""}
      showModalBodyScroll={!warehouseToUpdate}
    >
      <div className={classes.paperBody}>
        {warehouseToUpdate ? (
          <UITabMaterial
            leftPanels={panels}
            selectedLeftPanel={selectLeftPanel}
            onChangeLeftPanel={(_, value) => setSelectLeftPanel(Number(value))}
            isRenderingStatusField={true}
            statusFieldProps={{ ...statusFieldProps, label: "" }}
          />
        ) : (
          <UISteps steps={steps} currentStep={currentStep} stepsOption={options} />
        )}
      </div>
    </Modal>
  );
};

export default WarehouseModal;
